import currency from 'currency.js';
import { find, sumBy } from 'lodash';

import { HeaderStep } from '@/components/generic/Stepper/types';
import {
  AccessProgramType,
  CreateOrderItemVm,
  OpportunityVm,
  OrderDeliveryType,
  OrderInventoryType,
  PriceBookEntryVm,
  ProductFamily,
  ProductLine,
  ProductPricingType,
  ProductType,
  ProductVm,
  ProposalItemVm,
  ProposalVm,
  ShipmentMeasurement,
  SolarProductType,
} from '@/types';

import { isProductAvailable } from './components/OrderDetailsStep/components/SelectServices/constants';
import { calcWarrantyTotal } from './components/OrderDetailsStep/components/Warranty/helpers';
import { OrderItemWrapper, ProductLineName, State, StepsName, TaxableItem } from './types';

/**
 * Calculate initial linear feet from proposal item
 * @param proposalItem Proposal item to calculate linear feet from
 * @returns Number of linear feet
 */
const calculateLinearFeet = (proposalItem: ProposalItemVm): number => {
  const quantityRatio = proposalItem?.product?.linearFeetToQuantityRatio ?? 1;

  return Math.ceil(proposalItem.quantity * quantityRatio);
};

/**
 * Calculate initial number of bundles from proposal item
 * @param proposalItem Proposal item to calculate number of bundles from
 * @returns Number of bundles
 */
const calculateNumberOfBundles = (proposalItem: ProposalItemVm): number | null => {
  const bundleRatio = proposalItem?.product?.bundleToQuantityRatio ?? 1;

  return Math.ceil(proposalItem?.quantity / bundleRatio);
};

/**
 * Get number of bundles or ea from the equipment that will be shipped with the order
 * @param orderItem Order item wrapper to calculate quantity for
 * @returns Number of bundles or ea feet
 */
export const getShippingQuantity = (orderItem: OrderItemWrapper): string => {
  const product = orderItem.priceBookEntry.product;
  const quantity = orderItem.orderItem.quantity;

  if (product === null || product.shipmentMeasurement === null) {
    return String(quantity);
  }

  const unit = product.shipmentMeasurement === ShipmentMeasurement.EA ? 'EA' : 'BD';

  return `${orderItem.orderItem.numberOfBundles ?? quantity} ${unit}`;
};

export const mapProposalItemToOrderItem = (proposalItem: ProposalItemVm): CreateOrderItemVm => {
  return {
    productId: proposalItem.product.id,
    quantity: proposalItem.quantity,
    description: proposalItem.description,
    quantityOfOverage: proposalItem.quantityOfOverage ?? 0,
    quantityByDesign: proposalItem.quantityByDesign ?? 0,
    totalOutputKw: proposalItem.totalOutputKw,
    totalOutputW: proposalItem.totalOutputW,
    numberOfBundles:
      proposalItem?.product?.type === ProductType['Ridge Cap'] && !proposalItem.numberOfBundles
        ? calculateNumberOfBundles(proposalItem)
        : proposalItem.numberOfBundles,
    numberOfSquares: proposalItem.numberOfSquares,
    // since we do not allow users to create proposal items with linear feet,
    // we do not expect them in proposal items of synced proposal, therefore,
    // we set it to default value
    linearFeetValue:
      proposalItem?.product?.type === ProductType['Ridge Cap']
        ? calculateLinearFeet(proposalItem)
        : 0,
  };
};

export const convertToOrderItem = (
  pbe: PriceBookEntryVm,
  pi: ProposalItemVm,
): OrderItemWrapper => ({
  priceBookEntry: pbe,
  orderItem: mapProposalItemToOrderItem(pi),
  proposalItem: pi,
});

export const mapPriceBookItemToOrderItem = (priceBookItem: PriceBookEntryVm): CreateOrderItemVm => {
  return {
    productId: priceBookItem.product.id,
    quantity: priceBookItem.product.isAddOn ? 0 : 1,
    description: priceBookItem.product.description,
    quantityOfOverage: 0,
    quantityByDesign: 0,
    totalOutputKw: null,
    totalOutputW: null,
    numberOfBundles: null,
    numberOfSquares: null,
    linearFeetValue: 0,
  };
};

export const getOrderTotal = (
  state: State,
  productLineName: ProductLineName,
  proposal: ProposalVm | null,
): number => {
  const selectedServices = state.selectedServices;
  const isDesignServicesSelected = selectedServices === 'Design Services';
  const isCompleteServicesSelected = selectedServices === 'Complete Energy Services';

  const visibleOrderItems = state.visibleOrderItems;
  const inventoryType = state.order.inventoryType;

  const shouldIgnoreDesignServicesItem = (item: OrderItemWrapper) =>
    isCompleteServicesSelected &&
    (!isProductAvailable(item) || item.priceBookEntry.name === 'Design Services');

  const shouldPassOnlyDesignServices = (item: OrderItemWrapper) =>
    isDesignServicesSelected && item.priceBookEntry.name !== selectedServices;

  return sumBy(visibleOrderItems, (item: OrderItemWrapper) => {
    const shouldIgnoreService =
      isService(item) &&
      !isWarranty(item.priceBookEntry) &&
      item.priceBookEntry.product.isPreSelected &&
      (!selectedServices ||
        shouldPassOnlyDesignServices(item) ||
        shouldIgnoreDesignServicesItem(item));

    const shouldIgnoreInventory =
      isInventory(item) &&
      (productLineName === 'Decotech®' || inventoryType === OrderInventoryType['Need Inventory']);

    const shouldIgnoreItem =
      (!OLD_SERVICES_LIST_VIEW && shouldIgnoreService) || shouldIgnoreInventory;

    if (shouldIgnoreItem) {
      return 0;
    }

    return getOrderItemPrice(state, item, proposal);
  });
};

export const initialWizardHeaderSteps: HeaderStep[] = [
  {
    step: 1,
    label: StepsName['Order Details'],
  },
  {
    step: 2,
    label: StepsName['Shipping'],
  },
  {
    step: 3,
    label: StepsName['Confirmation'],
  },
  {
    step: 4,
    label: StepsName['Success'],
    isHidden: true,
  },
];

export const getInitialState = (): State => ({
  wizardHeaderSteps: [...initialWizardHeaderSteps],
  // we use this property to easily collect all order items before submitting the order
  visibleOrderItems: [],
  hiddenOrderItems: [],
  isAsphaltShingleSectionEnabled: false,
  isRidgeCapSectionEnabled: false,
  order: {
    opportunityId: '',
    // TODO: revise it once we get to work on design to orders
    partialReRoof: null,
    purchaseNumber: null,
    solarOnly: null,
    deliveryType: OrderDeliveryType['Homeowner/installation address'],
    deliveryInstruction: null,
    bucketId: null,
    shipmentContact: null,
    orderItems: [],
    inventoryType: null,
    totalContractAmount: null,
    roofSize: null,
    isFinancingIncluded: null,
    batteryPrice: null,
    projectOptions: null,
    additionalNotes: null,
    legalComplianceConfirmed: false,
    skipSheathingPresent: null,
    radiantBarrierInstalled: null,
    shingleColor: null,
    shingleName: null,
    sprayFoamInstallationPresent: null,
    distributionWarranty: null,
    watcherList: [],
  },
  // we use these two properties to track previous props for their comparison in getDerivedStateFromProps
  prevShippingAddressesLength: 0,
  prevProposalItemsLength: 0,
  isCancellationModalOpened: false,
  selectedServices: null,
  quantity: 0,
  esShingleQuantity: 0,
  isPriceHidden: false,
  isAccessProgram: false,
  isDistribution: false,
  adders: [],
  proposalSolarProductType: SolarProductType.TLS1_V1D,
});

export const isAccessOpportunity = (opportunity: OpportunityVm | null) =>
  opportunity?.accessProgram === AccessProgramType['Access'];

export const isAccessProgram = (opportunity: OpportunityVm | null, programAvailable: boolean) =>
  isAccessOpportunity(opportunity) && programAvailable;

export const isAsphaltShingle = (orderItemWrapper: OrderItemWrapper): boolean => {
  return orderItemWrapper?.priceBookEntry?.product?.type === ProductType['Asphalt Shingle'];
};

export const isRidgeCap = (orderItemWrapper: OrderItemWrapper): boolean => {
  return orderItemWrapper?.priceBookEntry?.product?.type === ProductType['Ridge Cap'];
};

export const isInventory = (orderItems: OrderItemWrapper): boolean => {
  return orderItems.priceBookEntry.product.isInventory;
};

export const isAddonProduct = (orderItems: OrderItemWrapper): boolean => {
  return Boolean(orderItems.priceBookEntry.product.isAddOn) && !isInventory(orderItems);
};

export const isService = (orderItems: OrderItemWrapper): boolean => {
  return (
    (orderItems.priceBookEntry.product.family === ProductFamily.Service ||
      orderItems.priceBookEntry.product.family === ProductFamily.Adder) &&
    !isAddonProduct(orderItems) &&
    !isInventory(orderItems) &&
    !isWarranty(orderItems.priceBookEntry)
  );
};

export const isEquipment = (orderItems: OrderItemWrapper): boolean => {
  return (
    orderItems.priceBookEntry.product.family === ProductFamily.Equipment &&
    !isStorageProduct(orderItems.priceBookEntry) &&
    !isAddonProduct(orderItems)
  );
};

export const isProductType = (orderItem: OrderItemWrapper, type: ProductType) =>
  orderItem.priceBookEntry.product.type === type;

export const productHasZeroQuantity = (orderItems: OrderItemWrapper): boolean => {
  return orderItems.orderItem.quantity === 0;
};

/**
 * Get order item price
 * @param item Order item with populated product and quantity
 * @param proposal
 * @returns Order item price
 */
export const getOrderItemPrice = (
  state: State,
  item: OrderItemWrapper,
  proposal: ProposalVm | null = null,
) => {
  if (
    proposal &&
    item.proposalItem === null &&
    item.priceBookEntry.pricingType === ProductPricingType['System Sized']
  ) {
    const pricePerWatt = item.priceBookEntry.pricePerWatt ?? 0;

    return currency(pricePerWatt).multiply((proposal.systemSize ?? 0) * 1000).value;
  }

  const roofSize = state?.order?.roofSize ?? 0;

  if (isWarranty(item.priceBookEntry)) {
    return calcWarrantyTotal(item.priceBookEntry, roofSize);
  }

  if (item.priceBookEntry.pricingType === ProductPricingType['Roof Sized']) {
    const pricePerSquare = item.priceBookEntry.pricePerSquare ?? 0;

    return currency(pricePerSquare).multiply(roofSize).value;
  }

  // proposal item unit price takes precedence over price book entry unit price
  const unitPrice = item.proposalItem?.unitPrice ?? item.priceBookEntry?.unitPrice ?? 0;

  if (item.proposalItem?.product?.bundleToQuantityRatio) {
    const quantity =
      (item.orderItem.numberOfBundles ?? 1) *
      (item.proposalItem.product.bundleToQuantityRatio ?? 1);

    return currency(unitPrice).multiply(quantity).value;
  }

  const quantity = item.orderItem?.quantity ?? 1;

  return currency(unitPrice).multiply(quantity).value;
};

/**
 * Get order item name
 * @param item Order item
 * @returns Either portal name or name of the related price book entry
 */
export const getOrderItemName = (item: OrderItemWrapper) => {
  return item.priceBookEntry?.product?.portalName ?? item.priceBookEntry?.product?.name ?? '';
};

export const getEquipmentName = (selectedAsphaltShingle: OrderItemWrapper | null) => {
  const isTimberlineSolar =
    selectedAsphaltShingle && getOrderItemName(selectedAsphaltShingle).includes('Timberline Solar');

  if (!selectedAsphaltShingle || isTimberlineSolar) {
    return 'Timberline Solar HDZ™';
  }

  return getOrderItemName(selectedAsphaltShingle);
};

export const OLD_SERVICES_LIST_VIEW = true;

export const isProductServiceOrInventory = (pbe: PriceBookEntryVm) =>
  pbe.product.family === ProductFamily.Service ||
  (pbe.product.family === ProductFamily.Equipment && pbe.product.isInventory);

export const isInventoryTypeOfEntry = (item: PriceBookEntryVm) =>
  item.product.family === ProductFamily.Equipment && item.product.isInventory;

export const isStorageProduct = (item: PriceBookEntryVm) =>
  item.product?.type === ProductType['Storage'];

export const isStorageOrderItem = (item: OrderItemWrapper) => isStorageProduct(item.priceBookEntry);

export const isWarranty = (item: PriceBookEntryVm): boolean => {
  return item.product.type === ProductType.Warranty;
};

export const isDifferentTypeOfProduct = (item: PriceBookEntryVm, isAccessProgram: boolean) =>
  (!OLD_SERVICES_LIST_VIEW && item.product.family === ProductFamily.Service) ||
  (item.isProductAvailable &&
    !isStorageProduct(item) &&
    (item.product.isAddOn ||
      (item.product.isPreSelected && isAccessProgram) ||
      isInventoryTypeOfEntry(item)));

export const isTimberlineSolar = (product?: ProductVm) =>
  product?.line === ProductLine['Timberline Solar'] && product?.type === ProductType.PV;

export const isEsProduct = (product?: ProductVm) =>
  product?.family === ProductFamily.Equipment && isTimberlineSolar(product);

export const hasTimberlineSolarItems = (proposalItem: ProposalItemVm) =>
  proposalItem.quantity > 0 && isTimberlineSolar(proposalItem.product);

export const shouldSkipInventoryItem = (
  inventoryType: OrderInventoryType | null,
  item: OrderItemWrapper,
  productLineName: ProductLineName | null,
): boolean =>
  isInventory(item) &&
  (inventoryType === OrderInventoryType['Need Inventory'] || productLineName === 'Decotech®');

export const shouldCalculateTaxForItem = (
  oi: OrderItemWrapper,
  inventoryType: OrderInventoryType | null,
): boolean => {
  const isEquipment =
    oi.priceBookEntry.product.family === ProductFamily.Equipment && !isInventory(oi);

  const shouldCalculateInventory =
    isInventory(oi) && inventoryType !== OrderInventoryType['Need Inventory'];

  return !productHasZeroQuantity(oi) && (isEquipment || shouldCalculateInventory);
};

export const getTaxableOrderItem = (
  state: State,
  wrappedOrderItem: OrderItemWrapper,
  proposal: ProposalVm | null,
): TaxableItem => {
  const { orderItem } = wrappedOrderItem;
  const { visibleOrderItems } = state;
  const productItem = find(visibleOrderItems, ['priceBookEntry.product.id', orderItem.productId]);

  return {
    SKU: productItem?.priceBookEntry?.product.stockKeepingUnit ?? '',
    price: getOrderItemPrice(state, wrappedOrderItem, proposal),
    commodityCode: productItem?.priceBookEntry.product.commodityCode ?? null,
  };
};
